import { SHADE_DEEP, TAU, WHITE, circle, glow, polygon, rim } from '../paint';
import type { Ctx, Piece } from '../paint';
import type { PixiPalette } from '../palette';
import { REFERENCE_RADIUS } from '../textures-looks';

export const UNIT = 2;

const APRON = '#2a2238';
const STRAP = '#6b5a4a';
const GLOVE = '#15151c';
const STEEL = '#cfd6e6';
const INK = '#04060e';
export const CREAM = 0xfff6dc;
const POTS = [
  [-7, -11, '#ff6fa8'],
  [-8.2, -7, '#3fd0c9'],
  [-8.6, -3, '#f5e663'],
] as const;

const ARM = { from: [1, -12.5], to: [13, -9], width: 5.4 } as const;
const HANDS = { along: 13.5, across: 9 } as const;
export const NEEDLE_TIP = { along: 21, across: 7.4 } as const;
export const KNOT = { along: -3, length: 6.5, spread: 2.6 } as const;
export const BANDS = { along: [3, 5.4, 7.8, 10.2], across: 12, slope: 0.75 } as const;

function units(draw: (ctx: Ctx) => void): (ctx: Ctx) => void {
  return (ctx) => {
    ctx.save();
    ctx.scale(UNIT, UNIT);
    draw(ctx);
    ctx.restore();
  };
}

function stroke(
  ctx: Ctx,
  color: string,
  width: number,
  points: readonly (readonly [number, number])[],
) {
  ctx.beginPath();
  for (const [x, y] of points) {
    ctx.lineTo(x, y);
  }
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.strokeStyle = color;
  ctx.lineWidth = width;
  ctx.stroke();
}

function arms(ctx: Ctx): void {
  for (const side of [-1, 1]) {
    const line = [
      [ARM.from[0], side * -ARM.from[1]],
      [ARM.to[0], side * -ARM.to[1]],
    ] as const;
    stroke(ctx, SHADE_DEEP, ARM.width + 2.6, line);
    stroke(ctx, WHITE, ARM.width, line);
  }
}

function band(ctx: Ctx): void {
  stroke(ctx, WHITE, 1.3, [
    [0, -2.2],
    [0, 2.2],
  ]);
}

function apronPath(ctx: Ctx): void {
  ctx.beginPath();
  ctx.ellipse(0, 0, 8.5, 14, 0, 0, TAU);
}

function apron(ctx: Ctx): void {
  apronPath(ctx);
  ctx.fillStyle = APRON;
  ctx.fill();
  ctx.lineWidth = 2;
  ctx.strokeStyle = INK;
  ctx.stroke();
  for (const side of [-1, 1]) {
    stroke(ctx, STRAP, 1.6, [
      [-6, side * 7],
      [6, side * 7],
    ]);
  }
  for (const [x, y, color] of POTS) {
    circle(ctx, 1.9, x, y);
    ctx.fillStyle = color;
    ctx.fill();
    ctx.lineWidth = 0.8;
    ctx.strokeStyle = INK;
    ctx.stroke();
  }
}

function silhouette(ctx: Ctx): void {
  apronPath(ctx);
  ctx.fillStyle = WHITE;
  ctx.fill();
}

function needle(ctx: Ctx): void {
  const line = [
    [HANDS.along, HANDS.across],
    [20, 7.5],
  ] as const;
  stroke(ctx, INK, 4.2, line);
  stroke(ctx, STEEL, 2.2, line);
  circle(ctx, 1.2, NEEDLE_TIP.along, NEEDLE_TIP.across);
  ctx.fillStyle = WHITE;
  ctx.fill();
}

function gloves(ctx: Ctx): void {
  for (const side of [-1, 1]) {
    circle(ctx, 3, HANDS.along, side * HANDS.across);
    ctx.fillStyle = GLOVE;
    ctx.fill();
    ctx.lineWidth = 1.3;
    ctx.strokeStyle = INK;
    ctx.stroke();
  }
}

function tail(ctx: Ctx): void {
  const line = [
    [0, 0],
    [-KNOT.length, 0],
  ] as const;
  stroke(ctx, SHADE_DEEP, 3.6, line);
  stroke(ctx, WHITE, 2, line);
}

function bandana(ctx: Ctx): void {
  circle(ctx, 6.6, 3, 0);
  ctx.fillStyle = WHITE;
  ctx.fill();
  rim(ctx, 1.4);
}

function dots(ctx: Ctx): void {
  ctx.fillStyle = WHITE;
  for (let dot = 0; dot < 6; dot += 1) {
    const angle = (dot * TAU) / 6;
    circle(ctx, 0.9, 3 + Math.cos(angle) * 3.8, Math.sin(angle) * 3.8);
    ctx.fill();
  }
}

export const SCALE_RADIUS = 3.55;
function scale(ctx: Ctx): void {
  circle(ctx, SCALE_RADIUS - 0.4);
  ctx.fillStyle = WHITE;
  ctx.fill();
  ctx.lineWidth = 0.8;
  ctx.strokeStyle = SHADE_DEEP;
  ctx.stroke();
}

function head(ctx: Ctx): void {
  for (const side of [-1, 1]) {
    const horn = [
      [-0.5, side * 2.4],
      [-3.5, side * 3.6],
      [-6.5, side * 3.2],
    ] as const;
    stroke(ctx, SHADE_DEEP, 2.4, horn);
    stroke(ctx, WHITE, 1.3, horn);
    stroke(ctx, WHITE, 0.7, [
      [5, side * 1.6],
      [3, side * 4.6],
      [-1, side * 6.4],
      [-4.5, side * 6],
    ]);
  }
  polygon(ctx, [
    [7.5, 0],
    [5, -1.9],
    [1.5, -3.4],
    [-2.5, -3],
    [-3.6, 0],
    [-2.5, 3],
    [1.5, 3.4],
    [5, 1.9],
  ]);
  ctx.fillStyle = WHITE;
  ctx.fill();
  ctx.lineWidth = 1;
  ctx.strokeStyle = SHADE_DEEP;
  ctx.stroke();
}

function eyes(ctx: Ctx): void {
  for (const side of [-1, 1]) {
    circle(ctx, 1, 1.4, side * 2);
    ctx.fillStyle = WHITE;
    ctx.fill();
    circle(ctx, 0.5, 1.8, side * 2);
    ctx.fillStyle = INK;
    ctx.fill();
  }
}

function legs(ctx: Ctx): void {
  for (const side of [-1, 1]) {
    const leg = [
      [-0.5, side * 1.5],
      [-1, side * 4.4],
      [1.2, side * 5.6],
    ] as const;
    stroke(ctx, SHADE_DEEP, 2.4, leg);
    stroke(ctx, WHITE, 1.2, leg);
  }
}

function tuft(ctx: Ctx): void {
  polygon(ctx, [
    [1, 0],
    [-4.5, -2.8],
    [-3, 0],
    [-4.5, 2.8],
  ]);
  ctx.fillStyle = WHITE;
  ctx.fill();
  ctx.lineWidth = 0.8;
  ctx.strokeStyle = SHADE_DEEP;
  ctx.stroke();
}

function soft(width: number, height: number): Piece {
  return [
    96,
    96,
    REFERENCE_RADIUS,
    (ctx) => {
      glow(ctx, WHITE, 8);
      ctx.fillStyle = WHITE;
      ctx.beginPath();
      ctx.ellipse(0, 0, width, height, 0, 0, TAU);
      ctx.fill();
    },
  ];
}

export const DRAGON_PIECES = {
  scale: [20, 20, REFERENCE_RADIUS, units(scale)],
  dragonHead: [36, 32, REFERENCE_RADIUS, units(head)],
  dragonEyes: [16, 16, REFERENCE_RADIUS, units(eyes)],
  dragonLegs: [16, 32, REFERENCE_RADIUS, units(legs)],
  dragonTuft: [24, 16, REFERENCE_RADIUS, units(tuft)],
} satisfies Record<string, Piece>;

export const TATOUE_PIECES = {
  arms: [72, 72, REFERENCE_RADIUS, units(arms)],
  band: [8, 16, REFERENCE_RADIUS, units(band)],
  silhouette: [48, 64, REFERENCE_RADIUS, units(silhouette)],
  apron: [48, 64, REFERENCE_RADIUS, units(apron)],
  needle: [96, 48, REFERENCE_RADIUS, units(needle)],
  gloves: [72, 56, REFERENCE_RADIUS, units(gloves)],
  tail: [36, 12, REFERENCE_RADIUS, units(tail)],
  bandana: [44, 40, REFERENCE_RADIUS, units(bandana)],
  dots: [40, 40, REFERENCE_RADIUS, units(dots)],
  shadow: soft(18, 28),
  ...DRAGON_PIECES,
} satisfies Record<string, Piece>;

export function inkColor(palette: PixiPalette, classColor: number, rank: number): number {
  const colors = [
    palette.mage,
    palette.turquoise,
    palette.healer,
    palette.noyau,
    classColor,
    palette.tank,
  ];
  return colors[rank % colors.length] ?? classColor;
}

export function tattooColor(palette: PixiPalette, upgradeId: string): number {
  const colors = [palette.mage, palette.turquoise, palette.healer, palette.noyau, palette.tank];
  let sum = 0;
  for (let index = 0; index < upgradeId.length; index += 1) {
    sum += upgradeId.charCodeAt(index);
  }
  return colors[sum % colors.length] ?? palette.texte;
}
